import $ from "jquery";
import assert from "minimalistic-assert";

import render_topic_permissions_modal from "../templates/topic_permissions_modal.hbs";

import * as dialog_widget from "./dialog_widget.ts";
import * as emoji_picker from "./emoji_picker.ts";
import * as group_setting_pill from "./group_setting_pill.ts";
import {$t, $t_html} from "./i18n.ts";
import * as people from "./people.ts";
import {stream_color_palette} from "./stream_color.ts";
import * as topic_appearance_picker from "./topic_appearance_picker.ts";
import * as topic_permissions from "./topic_permissions.ts";
import type {GroupSettingPillContainer} from "./typeahead_helper.ts";
import * as ui_report from "./ui_report.ts";
import * as user_group_pill from "./user_group_pill.ts";
import * as user_groups from "./user_groups.ts";
import * as user_pill from "./user_pill.ts";

let latest_request = 0;
let open_topic: {stream_id: number; topic: string; create_only: boolean} | undefined;

export function revalidate_streams(stream_ids: Set<number>): void {
    const target = open_topic;
    if (target === undefined || !stream_ids.has(target.stream_id)) {
        return;
    }
    topic_permissions.fetch_topic_permissions(target.stream_id, target.topic, (data) => {
        if (open_topic === target && (!data.can_manage || (target.create_only && !data.can_create))) {
            dialog_widget.close();
        }
    }, () => {
        if (open_topic === target) {
            dialog_widget.close();
        }
    }, target.create_only);
}

export function launch(
    stream_id: number,
    topic: string,
    create_only = false,
    is_current: () => boolean = () => true,
): void {
    const request = ++latest_request;
    topic_permissions.fetch_topic_permissions(stream_id, topic, (data) => {
        if (request !== latest_request || !is_current() || !data.can_manage ||
            (create_only && !data.can_create)) {
            return;
        }
        const palette = stream_color_palette.flat();
        open_topic = {stream_id, topic, create_only};
        const widgets = new Map<string, GroupSettingPillContainer>();
        const unlisted = new Map<string, topic_permissions.TopicPermissionAccess>();
        dialog_widget.launch({
            html_heading: $t_html({defaultMessage: "Topic settings"}),
            html_body: render_topic_permissions_modal({
                icon_colors: [
                    {value: palette[1]!, label: $t({defaultMessage: "Red"})},
                    {value: palette[5]!, label: $t({defaultMessage: "Orange"})},
                    {value: palette[7]!, label: $t({defaultMessage: "Green"})},
                    {value: palette[15]!, label: $t({defaultMessage: "Blue"})},
                    {value: palette[18]!, label: $t({defaultMessage: "Gray"})},
                    {value: palette[22]!, label: $t({defaultMessage: "Purple"})},
                ],
                color_fields: [
                    {name: "text_color_light", label: $t({defaultMessage: "Text color (light theme)"})},
                    {name: "text_color_dark", label: $t({defaultMessage: "Text color (dark theme)"})},
                    {name: "icon_color_light", label: $t({defaultMessage: "Icon color (light theme)"})},
                    {name: "icon_color_dark", label: $t({defaultMessage: "Icon color (dark theme)"})},
                ],
                is_whitelist: data.permission_mode === topic_permissions.topic_permission_modes.whitelist,
                is_blacklist: data.permission_mode === topic_permissions.topic_permission_modes.blacklist,
            }),
            close_on_submit: false,
            post_render() {
                topic_appearance_picker.initialize(data);
                $("#topic_permissions_history").val(data.history_public_to_subscribers === null
                    ? "inherit" : data.history_public_to_subscribers ? "shared" : "protected");
                $("#topic_permissions_description_override").prop("checked", data.description !== null);
                $("#topic_permissions_description").val(data.description ?? "")
                    .prop("disabled", data.description === null);
                $("#topic_permissions_description_override").on("change", function () {
                    $("#topic_permissions_description").prop("disabled", !$(this).prop("checked"));
                });
                for (const kind of ["read", "post", "manage"] as const) {
                    const $container = $(`#topic_permissions_${kind}_access`);
                    const widget = group_setting_pill.create_pills($container, "can_send_message_group", "stream");
                    widgets.set(kind, widget);
                    group_setting_pill.set_up_pill_typeahead({
                        pill_widget: widget, $pill_container: $container,
                        opts: {setting_name: "can_send_message_group", setting_type: "stream"},
                    });
                    const access = data[`${kind}_access`];
                    const hidden: topic_permissions.TopicPermissionAccess = {
                        direct_members: [], direct_subgroups: [],
                    };
                    unlisted.set(kind, hidden);
                    for (const user_id of access.direct_members) {
                        const user = people.maybe_get_user_by_id(user_id, true);
                        if (user) {
                            user_pill.append_user(user, widget, false);
                        } else {
                            hidden.direct_members.push(user_id);
                        }
                    }
                    for (const group_id of access.direct_subgroups) {
                        const group = user_groups.maybe_get_user_group_from_id(group_id);
                        if (group) {
                            user_group_pill.append_user_group(group, widget, false);
                        } else {
                            hidden.direct_subgroups.push(group_id);
                        }
                    }
                }
            },
            on_click() {
                const appearance = topic_appearance_picker.get();
                if (!appearance || !is_current()) {
                    ui_report.error(
                        $t_html({defaultMessage: "Check the topic, emoji, and colors before saving."}),
                        undefined, $("#dialog_error"),
                    );
                    dialog_widget.hide_dialog_spinner();
                    return;
                }
                const access = (kind: string): topic_permissions.TopicPermissionAccess => {
                    const widget = widgets.get(kind);
                    assert(widget !== undefined);
                    return {
                        direct_members: [...user_pill.get_user_ids(widget), ...unlisted.get(kind)!.direct_members].sort((a, b) => a - b),
                        direct_subgroups: [...user_group_pill.get_group_ids(widget), ...unlisted.get(kind)!.direct_subgroups].sort((a, b) => a - b),
                    };
                };
                const changed_access = Object.fromEntries(
                    (["read", "post", "manage"] as const).flatMap((kind) => {
                        const value = access(kind);
                        const original = data[`${kind}_access`];
                        const unchanged = JSON.stringify(value) === JSON.stringify({
                            direct_members: [...original.direct_members].sort((a, b) => a - b),
                            direct_subgroups: [...original.direct_subgroups].sort((a, b) => a - b),
                        });
                        return !create_only && unchanged ? [] : [[`${kind}_access`, value]];
                    }),
                );
                const mode = Number($("#topic_permissions_mode").val());
                const history = $("#topic_permissions_history").val() === "inherit"
                    ? null : $("#topic_permissions_history").val() === "shared";
                // New clients write the separate themes, not the legacy color.
                const {icon_color: _legacy_color, emoji: selected_emoji, ...theme_appearance} = appearance;
                const emoji_unchanged = selected_emoji === data.emoji || (
                    selected_emoji != null && data.emoji != null &&
                    selected_emoji.emoji_name === data.emoji.emoji_name &&
                    selected_emoji.emoji_code === data.emoji.emoji_code &&
                    selected_emoji.reaction_type === data.emoji.reaction_type
                );
                topic_permissions.update_topic_permissions(stream_id, topic, {
                    ...theme_appearance,
                    // Do not revalidate an unchanged emoji on unrelated edits.
                    // Submit new selections and preserve explicit resets.
                    ...(selected_emoji !== undefined &&
                        (create_only || appearance.icon !== data.icon || !emoji_unchanged)
                        ? {emoji: selected_emoji} : {}),
                    create_only,
                    icon_file: topic_appearance_picker.get_file(),
                    description: $("#topic_permissions_description_override").prop("checked")
                        ? String($("#topic_permissions_description").val()) : null,
                    ...changed_access,
                    ...(create_only || mode !== data.permission_mode ? {permission_mode: mode} : {}),
                    ...(create_only || history !== data.history_public_to_subscribers
                        ? {history_public_to_subscribers: history} : {}),
                }, () => dialog_widget.close(), (xhr) => {
                    ui_report.error($t_html({defaultMessage: "Failed"}), xhr, $("#dialog_error"));
                    dialog_widget.hide_dialog_spinner();
                });
            },
            on_hidden() {
                open_topic = undefined;
                emoji_picker.hide_emoji_popover();
            },
        });
    }, undefined, create_only);
}
